
import { useState, useMemo } from 'react';
import { useDataRoom } from '../context/DataRoomContext';
import { DocumentRequirement, ChecklistProgress, ChecklistFilter, ChecklistCategory } from '../types/checklist';
// import { DocumentType } from '../types/dataRoom';
// import { formatDocumentType, getDocumentCategory } from '@/utils/documentUtils';

export const useDocumentChecklist = () => {
  const { documents } = useDataRoom();
  const [filter, setFilter] = useState<ChecklistFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Define default document requirements
  const defaultRequirements: DocumentRequirement[] = useMemo(() => [
    // Financials
    {
      id: 'fin-audit-2024',
      category: 'Financials',
      documentType: 'audited_financials_2024_25',
      displayName: 'Audited Financials 2024-25',
      description: 'Latest audited financial statements',
      isRequired: true,
      isCompleted: false,
      priority: 'high',
      dueDate: new Date('2025-08-31'),
      uploadedDocuments: []
    },
    {
      id: 'fin-audit-2023',
      category: 'Financials',
      documentType: 'audited_financials_2023_24',
      displayName: 'Audited Financials 2023-24',
      description: 'Previous year audited financial statements',
      isRequired: true,
      isCompleted: false,
      priority: 'high',
      uploadedDocuments: []
    },
    {
      id: 'fin-audit-2022',
      category: 'Financials',
      documentType: 'audited_financials_2022_23',
      displayName: 'Audited Financials 2022-23',
      description: 'Previous year audited financial statements',
      isRequired: true,
      isCompleted: false,
      priority: 'high',
      uploadedDocuments: []
    },
    {
      id: 'fin-provisional',
      category: 'Financials',
      documentType: 'provisional_financials',
      displayName: 'Provisional Financials',
      description: 'Current provisional financial statements',
      isRequired: true,
      isCompleted: false,
      priority: 'medium',
      uploadedDocuments: []
    },
    {
      id: 'fin-mis',
      category: 'Financials',
      documentType: 'mis_financials',
      displayName: 'MIS Reports',
      description: 'Management Information System reports',
      isRequired: true,
      isCompleted: false,
      priority: 'low',
      uploadedDocuments: []
    },
    {
      id: 'fin-irr',
      category: 'Financials',
      documentType: 'itr',
      displayName: 'ITR',
      description: 'ITR Filling',
      isRequired: true,
      isCompleted: false,
      priority: 'low',
      uploadedDocuments: []
    },
    // Banking
    {
      id: 'bank-statements',
      category: 'Banking',
      documentType: 'bank_statements',
      displayName: 'Bank Statements',
      description: 'Last 12 months bank statements',
      isRequired: true,
      isCompleted: false,
      priority: 'high',
      uploadedDocuments: []
    },
    {
      id: 'gst-filing',
      category: 'Banking',
      documentType: 'gst_filing',
      displayName: 'GST Filing',
      description: 'GST returns and filings',
      isRequired: true,
      isCompleted: false,
      priority: 'medium',
      uploadedDocuments: []
    },
    {
      id: 'sanction-letter',
      category: 'Banking',
      documentType: 'sanction_letter',
      displayName: 'Sanction Letter',
      description: 'Sanction Letter',
      isRequired: true,
      isCompleted: false,
      priority: 'medium',
      uploadedDocuments: []
    },
    // KYC Documents
    {
      id: 'company-kyc',
      category: 'KYC Documents',
      documentType: 'company_kyc',
      displayName: 'Company KYC',
      description: 'Company registration and KYC documents',
      isRequired: true,
      isCompleted: false,
      priority: 'high',
      uploadedDocuments: []
    },
    {
      id: 'director-kyc',
      category: 'KYC Documents',
      documentType: 'director_kyc',
      displayName: 'Director KYC',
      description: 'Director identification documents',
      isRequired: true,
      isCompleted: false,
      priority: 'high',
      uploadedDocuments: []
    },
    {
      id: 'company-deck',
      category: 'KYC Documents',
      documentType: 'company_deck',
      displayName: 'Company Deck',
      description: 'Company presentation and overview',
      isRequired: true,
      isCompleted: false,
      priority: 'medium',
      uploadedDocuments: []
    },
    {
      id: 'asset-leasing',
      category: 'Other',
      documentType: 'asset_leasing',
      displayName: 'Asset Leasing',
      description: 'Asset Leasing',
      isRequired: true,
      isCompleted: false,
      priority: 'medium',
      uploadedDocuments: []
    },
  ], []);

  // Update requirements based on uploaded documents
  const requirements = useMemo(() => {
    return defaultRequirements.map(req => {
      const matchingDocs = documents.filter(doc => doc.type === req.documentType);
      const isCompleted = matchingDocs.length > 0;
      
      return {
        ...req,
        isCompleted,
        completedAt: isCompleted ? matchingDocs[0].uploadedAt : undefined,
        uploadedDocuments: matchingDocs
      };
    });
  }, [documents, defaultRequirements]);

  // Calculate overall progress
  const progress = useMemo((): ChecklistProgress => {
    const totalRequired = requirements.filter(req => req.isRequired).length;
    const completed = requirements.filter(req => req.isRequired && req.isCompleted).length;
    const inProgress = requirements.filter(req => req.isRequired && !req.isCompleted && req.uploadedDocuments.length > 0).length;
    
    const now = new Date();
    const overdue = requirements.filter(req => 
      req.isRequired && 
      !req.isCompleted && 
      req.dueDate && 
      req.dueDate < now
    ).length;

    return {
      totalRequired,
      completed,
      inProgress,
      overdue,
      percentage: totalRequired > 0 ? Math.round((completed / totalRequired) * 100) : 0
    };
  }, [requirements]);

  // Group requirements by category
  const categorizedRequirements = useMemo((): ChecklistCategory[] => {
    const categories = [...new Set(requirements.map(req => req.category))];
    
    return categories.map(categoryName => {
      const categoryReqs = requirements.filter(req => req.category === categoryName);
      const completed = categoryReqs.filter(req => req.isCompleted).length;
      const total = categoryReqs.length;
      
      return {
        name: categoryName,
        requirements: categoryReqs,
        progress: {
          completed,
          total,
          percentage: total > 0 ? Math.round((completed / total) * 100) : 0
        }
      };
    });
  }, [requirements]);

  // Filter requirements based on current filter and search
  const filteredRequirements = useMemo(() => {
    let filtered = requirements;

    // Apply filter
    switch (filter) {
      case 'pending':
        filtered = filtered.filter(req => !req.isCompleted);
        break;
      case 'complete':
        filtered = filtered.filter(req => req.isCompleted);
        break;
      case 'overdue':
        const now = new Date();
        filtered = filtered.filter(req => 
          !req.isCompleted && 
          req.dueDate && 
          req.dueDate < now
        );
        break;
      default:
        break;
    }

    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(req =>
        req.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [requirements, filter, searchTerm]);

  const isOverdue = (requirement: DocumentRequirement): boolean => {
    if (!requirement.dueDate || requirement.isCompleted) return false;
    return requirement.dueDate < new Date();
  };

  const getRequirementStatus = (requirement: DocumentRequirement) => {
    if (requirement.isCompleted) return 'complete';
    if (isOverdue(requirement)) return 'overdue';
    if (requirement.uploadedDocuments.length > 0) return 'in-progress';
    return 'not-started';
  };

  return {
    requirements,
    filteredRequirements,
    categorizedRequirements,
    progress,
    filter,
    setFilter,
    searchTerm,
    setSearchTerm,
    isOverdue,
    getRequirementStatus
  };
};
